import { Platform, PLATFORMS } from "@adora/shared/types/ad-platforms";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

// Interface for platform-specific data
interface PlatformData {
  campaignIds: string[];
  adgroupIds: string[];
}

// Store interface
interface ReportingTrackedIdsStore {
  // Data organized by platform
  platforms: Record<Platform, PlatformData>;

  // Action to initialize a platform with data
  initializePlatform: (platform: Platform, data?: Partial<PlatformData>) => void;

  // Actions to set data for a specific platform
  setCampaignIds: (platform: Platform, ids: string[]) => void;
  setAdgroupIds: (platform: Platform, ids: string[]) => void;
}

// Default data for a platform
const DEFAULT_PLATFORM_DATA: PlatformData = {
  campaignIds: [],
  adgroupIds: []
};

// Create the store
export const useReportingStore = create<ReportingTrackedIdsStore>()(
  devtools(
    (set) => ({
      // Initialize with empty data for all known platforms
      platforms: Object.values(PLATFORMS).reduce(
        (acc, platform) => ({
          ...acc,
          [platform]: { ...DEFAULT_PLATFORM_DATA }
        }),
        {} as Record<Platform, PlatformData>
      ),

      // Initialize a platform with specific data
      initializePlatform: (platform, data = {}) =>
        set((state) => ({
          platforms: {
            ...state.platforms,
            [platform]: {
              ...DEFAULT_PLATFORM_DATA,
              ...state.platforms[platform],
              ...data
            }
          }
        })),

      // Set campaign IDs for a specific platform
      setCampaignIds: (platform, ids) =>
        set((state) => ({
          platforms: {
            ...state.platforms,
            [platform]: {
              ...(state.platforms[platform] || DEFAULT_PLATFORM_DATA),
              campaignIds: ids
            }
          }
        })),

      // Set ad group IDs for a specific platform
      setAdgroupIds: (platform, ids) =>
        set((state) => ({
          platforms: {
            ...state.platforms,
            [platform]: {
              ...(state.platforms[platform] || DEFAULT_PLATFORM_DATA),
              adgroupIds: ids
            }
          }
        }))
    }),
    { name: "reporting-tracked-ids-store" }
  )
);
